import axios from 'axios'
import api from '..'
import { uploadFiles } from '../file'
import { uploadImages } from '../images'
import { WorkOrderConfigurationResponse, WorkOrdersResponse } from './types'
import { isNewRelicAgentError, logger } from '../../utils/logger'

interface WorkOrderItemRequest {
  work_order_type: string
  color?: string
  has_implant?: boolean
  implant_type?: string
  teeth?: number[]
  images?: string[]
  situation_model?: string
}

interface WorkOrderRequest {
  patient_id: number
  description: string
  estimated_delivery_date: string
  print_link: string[]
  order_items: WorkOrderItemRequest[]
}

export type WorkOrderItem = {
  type: string,
  color?: string,
  hasImplant: boolean,
  implantType?: string,
  teeth: number[],
  images?: FileList | null,
  situationModel?: string,
};

export interface WorkOrder {
  patient: number
  description: string
  printFile?: FileList | null
  estimatedDeliveryDate: string
  workOrderItems: WorkOrderItem[]
}

export const getCustomerWorkOrdersFn = async (page: string) => {
  try {
    const response = await api.get<WorkOrdersResponse>(`auth/customer/orders?page=${page}`)
    return response.data
  } catch (error) {
    logger.logError(error instanceof Error ? error : new Error('Get work orders error'), {
      type: 'get_work_orders_error',
      page
    })
    throw error
  }
}

export const createCustomerWorkOrderFn = async (
  {
    description,
    patient,
    printFile,
    estimatedDeliveryDate,
    workOrderItems
  }: WorkOrder
) => {
  const totalImages = workOrderItems.reduce((sum, item) => sum + (item.images?.length ?? 0), 0)

  logger.addPageAction('work_order_creation_start', {
    patientId: patient,
    itemsCount: workOrderItems.length,
    printFilesCount: printFile?.length ?? 0,
    totalImagesCount: totalImages
  })

  try {
    logger.addPageAction('work_order_step', { step: 'uploading_print_files' })
    const printFileUrls = await uploadFiles(printFile)

    logger.addPageAction('work_order_step', { step: 'uploading_item_images' })
    const newWorkOrderItemsPromises = workOrderItems.map(async (workOrderItem) => {
      const imagesUrls = await uploadImages(workOrderItem.images)
      const {
        type, implantType, situationModel, hasImplant, ...rest
      } = workOrderItem
      return {
        ...rest,
        images: imagesUrls,
        work_order_type: type,
        implant_type: implantType,
        situation_model: situationModel,
        has_implant: hasImplant
      }
    })

    const newWorkOrderItems = await Promise.all(newWorkOrderItemsPromises)

    const orderData = {
      patient_id: patient,
      description,
      estimated_delivery_date: estimatedDeliveryDate,
      print_link: printFileUrls,
      order_items: newWorkOrderItems
    }

    logger.addPageAction('work_order_step', { step: 'sending_order_request' })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any, any, WorkOrderRequest>('auth/customer/order', orderData)

    logger.addPageAction('work_order_creation_success', {
      patientId: patient,
      itemsCount: newWorkOrderItems.length,
      hasPrintFiles: printFileUrls.length > 0
    })

    return response.data
  } catch (error) {
    // Si el error es del agente de New Relic (nrWrapper) y no de la app,
    // ignorarlo y no romper el flujo del usuario
    if (isNewRelicAgentError(error)) {
      logger.addPageAction('work_order_nr_agent_error_ignored', {
        patientId: patient,
        errorMessage: error instanceof Error ? error.message : String(error)
      })
      return
    }

    const isNetworkError = axios.isAxiosError(error) && !error.response
    logger.logError(error instanceof Error ? error : new Error('Create work order error'), {
      type: 'create_work_order_error',
      isNetworkError,
      patientId: patient,
      itemsCount: workOrderItems.length,
      printFilesCount: printFile?.length ?? 0,
      totalImagesCount: totalImages,
      errorCode: axios.isAxiosError(error) ? error.code : undefined,
      responseStatus: axios.isAxiosError(error) ? error.response?.status : undefined
    })
    throw error
  }
}

export const getCustomerWorkOrdersConfigurationFn = async () => {
  try {
    const response = await api.get<WorkOrderConfigurationResponse>('auth/customer/work-order-configurations')
    return response.data
  } catch (error) {
    logger.logError(error instanceof Error ? error : new Error('Get work orders configuration error'), {
      type: 'get_work_orders_configuration_error'
    })
    throw error
  }
}

export const getCustomerWorkOrdersSummaryFn = async (page: string) => {
  try {
    const response = await api.get<WorkOrdersResponse>(`auth/customer/orders/in-progress?page=${page}&pageSize=30`)
    return response.data
  } catch (error) {
    logger.logError(error instanceof Error ? error : new Error('Get work orders summary error'), {
      type: 'get_work_orders_summary_error',
      page
    })
    throw error
  }
}
